/**
 * Agent 分配系统
 * 负责将任务分配给合适的 Agent（主执行者、审查者、咨询者）
 */

import { ExtendedTask, AgentAssignment } from '../types/extended-task';
import { AgentManager, Agent } from './agent-manager';
import { TaskStore } from './task-store';
import { logger } from '../utils/logger';
import { EventEmitter } from 'events';
import * as fs from 'fs-extra';
import * as path from 'path';

export interface AgentRole {
  role: 'primary' | 'reviewer' | 'consultant';
  agentId: string;
  reason: string;
  confidence: number; // 0-1 置信度
}

export interface AssignmentStrategy {
  name: string;
  evaluate(task: ExtendedTask, agents: Agent[]): AgentRole[];
}

export interface AssignmentResult {
  taskId: string;
  assignments: AgentAssignment;
  recommendations: AgentRole[];
  strategy: string;
  timestamp: Date;
}

export interface AgentPerformanceMetrics {
  agentId: string;
  tasksCompleted: number;
  averageCompletionTime: number;
  successRate: number;
  specialties: string[];
  lastActive: Date;
}

export class AgentAssignmentSystem extends EventEmitter {
  private agentManager: AgentManager;
  private taskStore: TaskStore;
  private strategies: Map<string, AssignmentStrategy> = new Map();
  private performanceMetrics: Map<string, AgentPerformanceMetrics> = new Map();
  
  constructor(workflowDir?: string) {
    super();
    this.agentManager = new AgentManager(workflowDir);
    this.taskStore = new TaskStore(workflowDir);
    
    // 注册默认策略
    this.registerDefaultStrategies();
  }
  
  /**
   * 初始化系统
   */
  async initialize(): Promise<void> {
    await this.agentManager.initialize();
    await this.taskStore.initialize();
    await this.loadPerformanceMetrics();
  }
  
  /**
   * 为任务分配 Agent
   */
  async assignAgentsToTask(
    taskId: string, 
    strategyName: string = 'skill-based'
  ): Promise<AssignmentResult> {
    const task = await this.taskStore.getTaskById(taskId);
    if (!task) {
      throw new Error(`Task ${taskId} not found`);
    }
    
    const agents = this.agentManager.getAgents();
    const strategy = this.strategies.get(strategyName);
    if (!strategy) {
      throw new Error(`Strategy ${strategyName} not found`);
    }
    
    // 使用策略评估 Agent
    const recommendations = strategy.evaluate(task, agents);
    
    // 构建分配结果
    const primaryRecommendation = recommendations.find(r => r.role === 'primary');
    const reviewerRecommendations = recommendations.filter(r => r.role === 'reviewer');
    const consultantRecommendations = recommendations.filter(r => r.role === 'consultant');
    
    if (!primaryRecommendation) {
      throw new Error('No suitable primary agent found');
    }
    
    const assignments: AgentAssignment = {
      primary: primaryRecommendation.agentId,
      reviewers: reviewerRecommendations.map(r => r.agentId),
      consultants: consultantRecommendations.map(r => r.agentId),
      assignedAt: new Date().toISOString(),
      reason: primaryRecommendation.reason
    };
    
    // 更新任务
    await this.taskStore.updateTask(taskId, {
      agentAssignments: assignments
    });
    
    const result: AssignmentResult = {
      taskId,
      assignments,
      recommendations,
      strategy: strategyName,
      timestamp: new Date()
    };
    
    // 触发事件
    this.emit('assignment', result);
    
    logger.info(`Assigned agents to task ${taskId}:`, {
      primary: assignments.primary,
      reviewers: assignments.reviewers,
      consultants: assignments.consultants
    });
    
    return result;
  }
  
  /**
   * 批量分配任务
   */
  async assignAgentsToBatch(
    taskIds: string[], 
    strategyName: string = 'skill-based'
  ): Promise<AssignmentResult[]> {
    const results: AssignmentResult[] = [];
    
    for (const taskId of taskIds) {
      try {
        const result = await this.assignAgentsToTask(taskId, strategyName);
        results.push(result);
      } catch (error) {
        logger.error(`Failed to assign agents to task ${taskId}:`, error);
      }
    }
    
    return results;
  }
  
  /**
   * 重新分配任务的 Agent
   */
  async reassignTask(
    taskId: string, 
    reason: string,
    strategyName?: string
  ): Promise<AssignmentResult> {
    const task = await this.taskStore.getTaskById(taskId);
    if (!task) {
      throw new Error(`Task ${taskId} not found`);
    }
    
    // 记录之前的分配
    const previousAssignment = task.agentAssignments;
    
    // 执行新的分配
    const result = await this.assignAgentsToTask(
      taskId, 
      strategyName || 'performance-based'
    );
    
    // 记录重新分配的原因
    await this.taskStore.updateTask(taskId, {
      metadata: {
        ...task.metadata,
        reassignments: [
          ...(task.metadata?.reassignments || []),
          {
            timestamp: new Date().toISOString(),
            reason,
            previousAssignment,
            newAssignment: result.assignments
          }
        ]
      }
    });
    
    logger.info(`Reassigned task ${taskId} due to: ${reason}`);
    
    return result;
  }
  
  /**
   * 获取 Agent 的任务分配情况
   */
  async getAgentAssignments(agentId: string): Promise<{
    asPrimary: ExtendedTask[];
    asReviewer: ExtendedTask[];
    asConsultant: ExtendedTask[];
  }> {
    const allTasks = await this.taskStore.getTasks();
    
    const asPrimary = allTasks.filter(
      t => t.agentAssignments?.primary === agentId
    );
    
    const asReviewer = allTasks.filter(
      t => t.agentAssignments?.reviewers?.includes(agentId)
    );
    
    const asConsultant = allTasks.filter(
      t => t.agentAssignments?.consultants?.includes(agentId)
    );
    
    return { asPrimary, asReviewer, asConsultant };
  }
  
  /**
   * 注册分配策略
   */
  registerStrategy(strategy: AssignmentStrategy): void {
    this.strategies.set(strategy.name, strategy);
    logger.info(`Registered assignment strategy: ${strategy.name}`);
  }
  
  /**
   * 获取所有策略
   */
  getStrategies(): string[] {
    return Array.from(this.strategies.keys());
  }
  
  /**
   * 更新 Agent 性能指标
   */
  async updateAgentPerformance(
    agentId: string, 
    taskId: string, 
    success: boolean,
    completionTime: number
  ): Promise<void> {
    const metrics = this.performanceMetrics.get(agentId) || {
      agentId,
      tasksCompleted: 0,
      averageCompletionTime: 0,
      successRate: 0,
      specialties: [],
      lastActive: new Date()
    };
    
    // 更新指标
    const totalTasks = metrics.tasksCompleted;
    const totalTime = metrics.averageCompletionTime * totalTasks;
    const totalSuccess = metrics.successRate * totalTasks;
    
    metrics.tasksCompleted++;
    metrics.averageCompletionTime = (totalTime + completionTime) / metrics.tasksCompleted;
    metrics.successRate = (totalSuccess + (success ? 1 : 0)) / metrics.tasksCompleted;
    metrics.lastActive = new Date();
    
    // 更新专长（基于成功完成的任务类型）
    if (success) {
      const task = await this.taskStore.getTaskById(taskId);
      if (task?.tags) {
        task.tags.forEach(tag => {
          if (!metrics.specialties.includes(tag)) {
            metrics.specialties.push(tag);
          }
        });
      }
    }
    
    this.performanceMetrics.set(agentId, metrics);
    await this.savePerformanceMetrics();
    
    this.emit('performance-update', { agentId, metrics });
  }
  
  /**
   * 获取 Agent 性能报告
   */
  getAgentPerformance(agentId?: string): AgentPerformanceMetrics | AgentPerformanceMetrics[] {
    if (agentId) {
      return this.performanceMetrics.get(agentId) || {
        agentId,
        tasksCompleted: 0,
        averageCompletionTime: 0,
        successRate: 0,
        specialties: [],
        lastActive: new Date()
      };
    }
    
    return Array.from(this.performanceMetrics.values());
  }
  
  /**
   * 注册默认策略
   */
  private registerDefaultStrategies(): void {
    // 基于技能的分配策略
    this.registerStrategy({
      name: 'skill-based',
      evaluate: (task: ExtendedTask, agents: Agent[]): AgentRole[] => {
        const roles: AgentRole[] = [];
        
        // 根据任务标签和类型匹配 Agent
        const taskTags = task.tags || [];
        const taskType = task.metadata?.subtype || 'general';
        
        // 评分每个 Agent
        const scores = agents.map(agent => {
          let score = 0;
          let reasons: string[] = [];
          
          // 检查任务类型匹配
          if (agent.taskTypes?.includes(taskType)) {
            score += 30;
            reasons.push(`擅长 ${taskType} 类型任务`);
          }
          
          // 检查能力匹配
          taskTags.forEach(tag => {
            const hasCapability = agent.capabilities.some(
              cap => cap.name.toLowerCase().includes(tag.toLowerCase())
            );
            if (hasCapability) {
              score += 20;
              reasons.push(`具备 ${tag} 能力`);
            }
          });
          
          // 检查工具匹配
          if (taskType === 'coding' && agent.tools.some(t => t.type === 'code')) {
            score += 15;
            reasons.push('配备代码工具');
          }
          
          return {
            agent,
            score,
            reason: reasons.join('、')
          };
        });
        
        // 排序并选择
        scores.sort((a, b) => b.score - a.score);
        
        // 选择主执行者
        if (scores[0]?.score > 0) {
          roles.push({
            role: 'primary',
            agentId: scores[0].agent.id,
            reason: scores[0].reason || '综合评分最高',
            confidence: Math.min(scores[0].score / 100, 1)
          });
        }
        
        // 选择审查者（与主执行者不同类别的高分 Agent）
        const primaryCategory = scores[0]?.agent.category;
        const reviewerCandidates = scores.filter(
          s => s.agent.category !== primaryCategory && s.score > 20
        );
        
        if (reviewerCandidates.length > 0) {
          roles.push({
            role: 'reviewer',
            agentId: reviewerCandidates[0].agent.id,
            reason: '跨领域审查视角',
            confidence: Math.min(reviewerCandidates[0].score / 100, 1)
          });
        }
        
        // 选择咨询者（特定专长的 Agent）
        const consultantCandidates = scores.filter(
          s => s.agent.id !== roles[0]?.agentId && 
               s.agent.id !== roles[1]?.agentId &&
               s.score > 15
        ).slice(0, 2);
        
        consultantCandidates.forEach(candidate => {
          roles.push({
            role: 'consultant',
            agentId: candidate.agent.id,
            reason: candidate.reason || '专业咨询',
            confidence: Math.min(candidate.score / 100, 1)
          });
        });
        
        return roles;
      }
    });
    
    // 基于性能的分配策略
    this.registerStrategy({
      name: 'performance-based',
      evaluate: (task: ExtendedTask, agents: Agent[]): AgentRole[] => {
        const roles: AgentRole[] = [];
        
        // 获取性能数据
        const agentScores = agents.map(agent => {
          const metrics = this.performanceMetrics.get(agent.id);
          const score = metrics 
            ? (metrics.successRate * 50 + 
               (1 / (metrics.averageCompletionTime || 1)) * 30 +
               metrics.tasksCompleted * 0.2)
            : 0;
          
          return { agent, score, metrics };
        });
        
        // 按性能排序
        agentScores.sort((a, b) => b.score - a.score);
        
        // 分配角色
        if (agentScores[0]) {
          roles.push({
            role: 'primary',
            agentId: agentScores[0].agent.id,
            reason: `高成功率 ${(agentScores[0].metrics?.successRate || 0) * 100}%`,
            confidence: Math.min(agentScores[0].score / 100, 1)
          });
        }
        
        // 选择审查者和咨询者
        agentScores.slice(1, 4).forEach((item, index) => {
          roles.push({
            role: index === 0 ? 'reviewer' : 'consultant',
            agentId: item.agent.id,
            reason: `性能优秀，完成 ${item.metrics?.tasksCompleted || 0} 个任务`,
            confidence: Math.min(item.score / 100, 1)
          });
        });
        
        return roles;
      }
    });
    
    // 负载均衡策略
    this.registerStrategy({
      name: 'load-balanced',
      evaluate: (task: ExtendedTask, agents: Agent[]): AgentRole[] => {
        const roles: AgentRole[] = [];
        
        // 计算每个 Agent 的当前负载
        const loadPromises = agents.map(async agent => {
          const assignments = await this.getAgentAssignments(agent.id);
          const load = assignments.asPrimary.filter(
            t => t.status === 'in_progress' || t.status === 'todo'
          ).length;
          
          return { agent, load };
        });
        
        // 这里简化处理，实际应该等待 Promise
        const agentLoads = agents.map(agent => {
          const metrics = this.performanceMetrics.get(agent.id);
          const recentTasks = metrics?.tasksCompleted || 0;
          
          return { 
            agent, 
            load: recentTasks, // 简化的负载计算
            capacity: 5 - recentTasks // 假设每个 Agent 容量为 5
          };
        });
        
        // 按可用容量排序
        agentLoads.sort((a, b) => b.capacity - a.capacity);
        
        // 分配给负载最轻的 Agent
        if (agentLoads[0]?.capacity > 0) {
          roles.push({
            role: 'primary',
            agentId: agentLoads[0].agent.id,
            reason: `当前负载最轻，可用容量 ${agentLoads[0].capacity}`,
            confidence: agentLoads[0].capacity / 5
          });
        }
        
        // 选择其他角色
        agentLoads.slice(1, 4).forEach((item, index) => {
          if (item.capacity > 0) {
            roles.push({
              role: index === 0 ? 'reviewer' : 'consultant',
              agentId: item.agent.id,
              reason: `有可用容量 ${item.capacity}`,
              confidence: item.capacity / 5
            });
          }
        });
        
        return roles;
      }
    });
  }
  
  /**
   * 加载性能指标
   */
  private async loadPerformanceMetrics(): Promise<void> {
    const metricsPath = path.join(
      this.agentManager['workflowDir'], 
      'agent-performance.json'
    );
    
    try {
      if (await fs.pathExists(metricsPath)) {
        const data = await fs.readJson(metricsPath);
        if (data.metrics) {
          data.metrics.forEach((metric: AgentPerformanceMetrics) => {
            this.performanceMetrics.set(metric.agentId, {
              ...metric,
              lastActive: new Date(metric.lastActive)
            });
          });
        }
      }
    } catch (error) {
      logger.error('Failed to load agent performance metrics:', error);
    }
  }
  
  /**
   * 保存性能指标
   */
  private async savePerformanceMetrics(): Promise<void> {
    const metricsPath = path.join(
      this.agentManager['workflowDir'], 
      'agent-performance.json'
    );
    
    const data = {
      metrics: Array.from(this.performanceMetrics.values()),
      updatedAt: new Date().toISOString()
    };
    
    try {
      await fs.writeJson(metricsPath, data, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save agent performance metrics:', error);
    }
  }
}